import { useState, useEffect, useCallback, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useAuth } from "@/hooks/use-auth";

type ConnectionStatus = "disconnected" | "scanning" | "connected";

interface WhatsAppConnectStepProps {
  readonly onComplete: () => void;
  readonly onSkip: () => void;
}

const STATUS_CONFIG: Record<ConnectionStatus, { color: string; label: string }> = {
  disconnected: { color: "bg-red-500", label: "\u05DC\u05D0 \u05DE\u05D7\u05D5\u05D1\u05E8" },
  scanning: { color: "bg-yellow-500", label: "\u05E1\u05D5\u05E8\u05E7\u05D9\u05DD..." },
  connected: { color: "bg-green-500", label: "\u05DE\u05D7\u05D5\u05D1\u05E8!" },
};

export function WhatsAppConnectStep({ onComplete, onSkip }: WhatsAppConnectStepProps): ReactNode {
  const { accessToken } = useAuth();
  const [status, setStatus] = useState<ConnectionStatus>("disconnected");
  const [qrData, setQrData] = useState<string | null>(null);

  const fetchQR = useCallback(async () => {
    try {
      const res = await fetch("/api/whatsapp/qr", {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) return;
      const data = (await res.json()) as { qr?: string; status?: string };
      if (data.qr) {
        setQrData(data.qr);
        setStatus("scanning");
      }
      if (data.status === "connected") {
        setStatus("connected");
      }
    } catch {
      // Non-critical — bridge may not be configured yet
    }
  }, [accessToken]);

  useEffect(() => {
    fetchQR();
  }, [fetchQR]);

  // Poll status while scanning
  useEffect(() => {
    if (status !== "scanning") return;
    const interval = setInterval(async () => {
      try {
        const res = await fetch("/api/whatsapp/status", {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (!res.ok) return;
        const data = (await res.json()) as { status?: string };
        if (data.status === "connected") {
          setStatus("connected");
        }
      } catch {
        // Ignore polling errors
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [status, accessToken]);

  const statusConfig = STATUS_CONFIG[status];

  return (
    <div className="flex flex-col gap-6 px-4 py-6">
      <h2 className="text-xl font-bold">{"\u05D7\u05D9\u05D1\u05D5\u05E8 \u05D5\u05D5\u05D0\u05D8\u05E1\u05D0\u05E4"}</h2>
      <p className="text-sm text-muted-foreground">
        {"\u05D7\u05D1\u05E8\u05D5 \u05D0\u05EA \u05D4\u05D5\u05D5\u05D0\u05D8\u05E1\u05D0\u05E4 \u05E9\u05DC\u05DB\u05DD \u05DC\u05E7\u05D1\u05DC\u05EA \u05D4\u05D5\u05D3\u05E2\u05D5\u05EA \u05E2\u05DC \u05D4\u05D6\u05DE\u05E0\u05D5\u05EA \u05D5\u05DC\u05D9\u05D3\u05D9\u05DD \u05D7\u05D3\u05E9\u05D9\u05DD."}
      </p>

      <Card>
        <CardContent className="flex flex-col items-center gap-4 p-4">
          {/* Status indicator */}
          <div className="flex items-center gap-2">
            <div className={`h-3 w-3 rounded-full ${statusConfig.color}`} />
            <span className="text-sm font-medium">{statusConfig.label}</span>
          </div>

          {/* QR Code area */}
          {status === "disconnected" && (
            <div className="flex h-48 w-48 items-center justify-center rounded-lg border-2 border-dashed border-muted-foreground/25">
              <Button variant="outline" className="min-h-[44px]" onClick={fetchQR}>
                {"\u05D4\u05E6\u05D2 QR"}
              </Button>
            </div>
          )}

          {status === "scanning" && qrData !== null && (
            <div className="flex flex-col items-center gap-3">
              <div className="flex h-48 w-48 items-center justify-center rounded-lg border bg-white p-2">
                <img src={qrData} alt="WhatsApp QR" className="h-full w-full" />
              </div>
              <p className="text-center text-xs text-muted-foreground">
                {"\u05E4\u05EA\u05D7\u05D5 \u05D0\u05EA \u05D4\u05D5\u05D5\u05D0\u05D8\u05E1\u05D0\u05E4 \u2192 \u05DE\u05DB\u05E9\u05D9\u05E8\u05D9\u05DD \u05DE\u05E7\u05D5\u05E9\u05E8\u05D9\u05DD \u2192 \u05E1\u05E8\u05E7\u05D5 \u05D0\u05EA \u05D4\u05E7\u05D5\u05D3"}
              </p>
            </div>
          )}

          {status === "connected" && (
            <div className="flex flex-col items-center gap-2 py-4">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-green-500">
                <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
                <path d="M22 4L12 14.01l-3-3" />
              </svg>
              <p className="font-medium text-green-600">{"\u05D4\u05D5\u05D5\u05D0\u05D8\u05E1\u05D0\u05E4 \u05DE\u05D7\u05D5\u05D1\u05E8 \u05D1\u05D4\u05E6\u05DC\u05D7\u05D4!"}</p>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex gap-3">
        <Button variant="outline" className="min-h-[44px] flex-1" onClick={onSkip}>
          {"\u05D3\u05DC\u05D2 \u05DC\u05E2\u05DB\u05E9\u05D9\u05D5"}
        </Button>
        {status === "connected" && (
          <Button className="min-h-[44px] flex-1" onClick={onComplete}>
            {"\u05D4\u05DE\u05E9\u05DA"}
          </Button>
        )}
      </div>
    </div>
  );
}
